import {toRaw} from "vue";
import {BUSINESS_DOCUMENT_TYPE_ENUM} from "@/models/businessDocumentTypeEnum";
import {
    BUSINESS_STATUS_ENUM,
    BUSINESS_STATUS_PIPELINE_BY_DOCUMENT_TYPE,
    TERMINAL_BUSINESS_STATUSES,
    getBusinessStatusTransitions,
    possibleBusinessStatusSelect,
} from "@/models/businessStatusEnum";
import {
    CREDIT_NOTE_STATUS_PIPELINE,
    TERMINAL_CREDIT_NOTE_STATUSES,
    possibleCreditNoteStatusSelect,
} from "@/models/creditNoteStatusEnum";
import {
    DEPOSIT_STATUS_PIPELINE,
    TERMINAL_DEPOSIT_STATUSES,
    getDepositStatus,
    possibleDepositStatusSelect,
} from "@/models/depositStatusEnum";
import {STATUS_DOCUMENT_KIND} from "@/components/businessEdition/status/statusTransition";

const KIND_LABEL = {
    [STATUS_DOCUMENT_KIND.QUOTE]: 'du devis',
    [STATUS_DOCUMENT_KIND.INVOICE]: 'de la facture',
    [STATUS_DOCUMENT_KIND.CREDIT_NOTE]: "de l'avoir",
    [STATUS_DOCUMENT_KIND.DEPOSIT]: "de l'acompte",
};

// Type de document affiché (onglet consulté) -> STATUS_DOCUMENT_KIND.
// Le workflow dépend de l'onglet consulté, pas du type réel du business.
// documentType est la valeur string de l'enum (selectedDocument.type, ex. 'QUOTE'), mais on accepte aussi
// l'objet enum (éventuellement Proxy réactif, d'où toRaw). Aucun défaut silencieux : un type inconnu
// n'est PAS traité comme une facture.
export function getStatusDocumentKind(documentType) {
    const raw = toRaw(documentType);
    const value = raw && typeof raw === 'object' ? raw.value : raw;
    switch (value) {
        case BUSINESS_DOCUMENT_TYPE_ENUM.QUOTE.value: return STATUS_DOCUMENT_KIND.QUOTE;
        case BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value: return STATUS_DOCUMENT_KIND.INVOICE;
        case BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value: return STATUS_DOCUMENT_KIND.CREDIT_NOTE;
        case BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value: return STATUS_DOCUMENT_KIND.DEPOSIT;
        default: return undefined;
    }
}

// Un business terminé (DONE) n'est plus modifiable, sauf pour l'annuler (Annulé reste atteignable).
export function isBusinessReadOnly(business) {
    return business.status === BUSINESS_STATUS_ENUM.DONE.value
        && !possibleBusinessStatusSelect(business).some(option => option.value !== business.status);
}

// Statut final : plus aucune étape du workflow normal n'est atteignable depuis le statut réel.
// Les issues "hors workflow" (Annulé...) sont des actions exceptionnelles : elles ne comptent pas.
function isFinalStatus({pipeline, savedStatus, transitions}) {
    const selectable = new Set(transitions.map(option => option.value));
    return !!savedStatus && !pipeline.some(status => status.value !== savedStatus && selectable.has(status.value));
}

// Étapes affichées (frise + issues) avec, pour chacune, son état déjà calculé.
//   current    : statut réel enregistré (jamais la sélection en cours)
//   selected   : sélection en cours (porte le background), purement visuel
//   pending    : sélection différente du statut réel
//   selectable : atteignable depuis le statut réel (transitions), y compris le statut réel lui-même
function buildSteps({pipeline, terminals, savedStatus, selectedStatus, pendingStatus, transitions}) {
    // Statut final : on n'affiche que le statut actuel (plus l'issue exceptionnelle éventuelle), pas la frise complète.
    if (isFinalStatus({pipeline, savedStatus, transitions})) {
        pipeline = pipeline.filter(status => status.value === savedStatus);
    }
    const selectable = new Set(transitions.map(option => option.value));
    // Issues "hors workflow" (Annulé...) : uniquement si atteignables ou si c'est le statut courant.
    const terminalSteps = terminals.filter(status => status.value === savedStatus || selectable.has(status.value));
    const savedIndex = pipeline.findIndex(status => status.value === savedStatus);
    const lastPipelineValue = pipeline.length ? pipeline[pipeline.length - 1].value : undefined;

    const toStep = (status, index, isTerminal) => {
        const isCurrent = status.value === savedStatus;
        const isDone = !isTerminal && savedIndex > -1 && index < savedIndex;
        const isAvailable = !isCurrent && selectable.has(status.value);
        const isPending = status.value === pendingStatus;
        const isLocked = !isCurrent && !isDone && !isAvailable;

        return {
            ...status,
            isCurrent, isDone, isAvailable, isLocked, isPending, isTerminal,
            isSelectable: selectable.has(status.value),
            isSelected: status.value === selectedStatus && !isCurrent,
            // Le statut réel est cliquable pour annuler la sélection d'une issue.
            isRevertable: isCurrent && !!pendingStatus && selectable.has(status.value),
            isTerminalFirst: terminalSteps.length > 0 && status.value === terminalSteps[0].value,
            isBeforeTerminal: terminalSteps.length > 0 && status.value === lastPipelineValue,
            title: getStepTitle({isCurrent, isPending, isDone, isAvailable}, !!pendingStatus),
        };
    };

    return [
        ...pipeline.map((status, index) => toStep(status, index, false)),
        ...terminalSteps.map((status, index) => toStep(status, index, true)),
    ];
}

function getStepTitle({isCurrent, isPending, isDone, isAvailable}, hasPendingChange) {
    if (isCurrent) return hasPendingChange ? 'Cliquer pour annuler la sélection' : 'Étape actuelle';
    if (isPending) return 'Sélectionné (enregistré à la validation)';
    if (isDone) return 'Étape déjà passée';
    if (isAvailable) return 'Cliquer pour passer à cette étape';
    return "Cette étape n'est pas accessible depuis le statut actuel";
}

// Workflow affiché pour le document consulté.
//   kind           : STATUS_DOCUMENT_KIND (cf. getStatusDocumentKind)
//   selectedStatus : sélection en cours (inputs.value, ou creditNoteInputs.status.value pour un avoir)
// Retourne {label, managed, readOnly, isFinal, savedStatus, pendingStatus, steps}.
// Le workflow (étape actuelle + transitions) ne dépend QUE du statut enregistré : sélectionner une étape
// ou une issue ne retire ni ne recalcule aucune option.
export function getStatusWorkflow({kind, business, creditNote, selectedStatus}) {
    const label = KIND_LABEL[kind] || 'du document';

    if (!kind) {
        return {label, managed: false, readOnly: true, isFinal: false, savedStatus: undefined, pendingStatus: undefined, steps: []};
    }

    if (kind === STATUS_DOCUMENT_KIND.CREDIT_NOTE) {
        // Un avoir se base uniquement sur creditNote.status (jamais business.status).
        const savedStatus = creditNote && creditNote.status;
        const pendingStatus = (selectedStatus && selectedStatus !== savedStatus) ? selectedStatus : undefined;
        const transitions = creditNote ? possibleCreditNoteStatusSelect(creditNote) : [];
        return {
            label, managed: true, savedStatus, pendingStatus,
            isFinal: isFinalStatus({pipeline: CREDIT_NOTE_STATUS_PIPELINE, savedStatus, transitions}),
            readOnly: !creditNote || !transitions.some(option => option.value !== savedStatus),
            steps: buildSteps({pipeline: CREDIT_NOTE_STATUS_PIPELINE, terminals: TERMINAL_CREDIT_NOTE_STATUSES, savedStatus, selectedStatus, pendingStatus, transitions}),
        };
    }

    if (kind === STATUS_DOCUMENT_KIND.DEPOSIT) {
        // Un acompte se base uniquement sur business.depositStatus (jamais business.status).
        const savedStatus = getDepositStatus(business);
        const pendingStatus = (selectedStatus && selectedStatus !== savedStatus) ? selectedStatus : undefined;
        const transitions = savedStatus ? possibleDepositStatusSelect(business) : [];
        return {
            label, managed: true, savedStatus, pendingStatus,
            isFinal: isFinalStatus({pipeline: DEPOSIT_STATUS_PIPELINE, savedStatus, transitions}),
            readOnly: !savedStatus || !transitions.some(option => option.value !== savedStatus),
            steps: buildSteps({pipeline: DEPOSIT_STATUS_PIPELINE, terminals: TERMINAL_DEPOSIT_STATUSES, savedStatus, selectedStatus, pendingStatus, transitions}),
        };
    }

    // Devis / facture. Onglet Devis d'un business déjà facturé : workflow figé (géré par getBusinessStatusTransitions).
    const view = getBusinessStatusTransitions(business, selectedStatus, kind);
    const isQuoteViewOfInvoice = kind === STATUS_DOCUMENT_KIND.QUOTE && !!business.invoiceNumber;
    return {
        label, managed: true,
        savedStatus: view.savedStatus,
        pendingStatus: view.pendingStatus,
        isFinal: isFinalStatus({pipeline: BUSINESS_STATUS_PIPELINE_BY_DOCUMENT_TYPE[kind], savedStatus: view.savedStatus, transitions: view.transitions}),
        readOnly: isQuoteViewOfInvoice || isBusinessReadOnly(business),
        steps: buildSteps({
            pipeline: BUSINESS_STATUS_PIPELINE_BY_DOCUMENT_TYPE[kind],
            terminals: TERMINAL_BUSINESS_STATUSES,
            ...view,
        }),
    };
}